import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { usePoll } from '@/hooks/poll/usePoll';
import { MessageSquare, BarChart3 } from 'lucide-react';

interface PollSectionProps {
  interviewId: string;
}

export const PollSection: React.FC<PollSectionProps> = ({ interviewId }) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState<string>('');
  const {
    poll,
    responses,
    userResponse,
    isLoading,
    isSubmitting,
    error,
    submitResponse
  } = usePoll(interviewId);

  const pollOptions = poll ? [
    { value: 'Positive', label: poll.option_positive || 'Positivt svar' },
    { value: 'Negative', label: poll.option_negative || 'Negativt svar' },
    { value: 'Vet ikke', label: 'Vet ikke' }
  ] : [
    { value: 'Positive', label: 'Positivt svar' },
    { value: 'Negative', label: 'Negativt svar' },
    { value: 'Vet ikke', label: 'Vet ikke' }
  ];

  const handleOptionSelect = async (option: string) => {
    setSelectedOption(option);
    await submitResponse(option);
  };

  // Calculate statistics
  const getStatistics = () => {
    const total = responses.length;
    if (total === 0) return null;

    const counts = {
      'Positive': responses.filter(r => r.response === 'Positive').length,
      'Negative': responses.filter(r => r.response === 'Negative').length,
      'Vet ikke': responses.filter(r => r.response === 'Vet ikke').length
    };

    return {
      total,
      counts,
      percentages: {
        'Positive': Math.round((counts.Positive / total) * 100),
        'Negative': Math.round((counts.Negative / total) * 100),
        'Vet ikke': Math.round((counts['Vet ikke'] / total) * 100)
      }
    };
  };

  const statistics = getStatistics();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error && !poll) {
    return (
      <Card className="p-6">
        <CardContent>
          <p className="text-destructive text-center">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!poll) {
    return (
      <Card className="p-6">
        <CardContent>
          <p className="text-muted-foreground text-center">
            Ingen avstemning tilgjengelig for denne artikkelen.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Poll Question */}
      <Card className="md:border md:shadow-sm border-0 shadow-none">
        <CardHeader className="px-0 md:px-6">
          <CardTitle className="flex items-center gap-2 text-base md:text-lg">
            <MessageSquare className="h-4 w-4 md:h-5 md:w-5" />
            Hva mener du?
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0 md:px-6">
          <p className="text-base md:text-lg mb-4 md:mb-6">{poll.question}</p>
          
          {/* Poll Options or User's Response */}
          {userResponse ? (
            <div className="space-y-4 md:space-y-6">
              <div className="p-3 md:p-4 bg-muted rounded-lg">
                <p className="font-medium text-xs md:text-sm text-muted-foreground mb-1 md:mb-2">Ditt svar:</p>
                <p className="font-medium text-sm md:text-base">{userResponse}</p>
              </div>
              
              {/* Show Statistics */}
              {statistics && (
                <div className="space-y-3 md:space-y-4">
                  <h3 className="font-medium text-sm md:text-base flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Resultater ({statistics.total} {statistics.total === 1 ? 'svar' : 'svar'})
                  </h3>
                  <div className="space-y-2 md:space-y-3">
                    {pollOptions.map((option) => {
                      const count = statistics.counts[option.value as keyof typeof statistics.counts];
                      const percentage = statistics.percentages[option.value as keyof typeof statistics.percentages];
                      return (
                        <div key={option.value} className="space-y-1.5 md:space-y-2">
                          <div className="flex justify-between text-xs md:text-sm">
                            <span className="pr-2">{option.label}</span>
                            <span className="font-medium whitespace-nowrap">{count} ({percentage}%)</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div 
                              className="bg-primary h-2 rounded-full transition-all duration-300"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3 md:space-y-4">
              <div className="space-y-2 md:space-y-3">
                {pollOptions.map((option) => (
                  <Button
                    key={option.value}
                    variant="outline"
                    className="w-full justify-start text-left p-3 md:p-4 h-auto text-sm md:text-base min-w-0"
                    onClick={() => handleOptionSelect(option.value)}
                    disabled={isSubmitting}
                  >
                    <span className="break-words whitespace-normal w-full">{option.label}</span>
                  </Button>
                ))}
              </div>
              
              {error && (
                <p className="text-destructive text-sm">{error}</p>
              )}
              
              {isSubmitting && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
};